import React from 'react';
import alpacaDarkBackgroundLogo from '../../assets/broker-logos/alpaca-dark-background.png';
import bitunixLogo from '../../assets/broker-logos/bitunix.webp';
import coinbaseLogo from '../../assets/broker-logos/coinbase.webp';
import krakenLogo from '../../assets/broker-logos/kraken.webp';
import schwabLogo from '../../assets/broker-logos/schwab.webp';
import tradierLogo from '../../assets/broker-logos/tradier.webp';
import tradovateLogo from '../../assets/broker-logos/tradovate.webp';
import Reveal from './Reveal';
import './courseKit.css';

/**
 * "Tools and brokers you'll work with".
 *
 * TWO problems were solved here, both of which had been fixed badly before.
 *
 * 1. CHIPS. Earlier versions put each mark on its own chip, first a mix of
 *    white and navy chips picked per logo, then all white. Both read as a grid
 *    of mismatched boxes with small logos marooned in them. There are no chips
 *    now. The whole section is a light band and the marks sit directly on it,
 *    which is what every credible logo wall does.
 *
 * 2. SIZE. Two separate things were wrong here, and fixing only the first is
 *    why this kept coming back.
 *
 *    (a) ASPECT. These marks range from 2:1 (Schwab, Alpaca) to nearly 8:1
 *        (NinjaTrader, IBKR). A shared height makes the wide ones enormous; a
 *        shared width makes them spindly. So each carries its own height,
 *        normalised to a constant optical AREA rather than a constant edge.
 *
 *    (b) PADDING INSIDE THE ASSET. The four tool wordmarks were authored on a
 *        200x32 canvas with the artwork filling only 41-67% of the width and
 *        49-75% of the height, while every broker PNG fills its frame edge to
 *        edge. So identical CSS produced wildly different rendered ink and
 *        Claude looked half the size of Coinbase. Their viewBoxes are now
 *        tightened to their measured getBBox, so ink == frame for all twelve.
 *
 *    Heights are then sqrt(area / inkAspect), with a light density correction
 *    so a solid tile like Schwab (89% ink coverage) is not visually heavier
 *    than a hairline wordmark like IBKR (24%). The correction is deliberately
 *    weak. An earlier stronger version over-shrank the solid tiles.
 *
 *    Target area ~14,200px², width capped at 312px. Measure with a canvas
 *    ink-bbox pass if you swap an asset; do not eyeball it.
 *
 * Assets are the real brand files, with dark-ink counterparts generated for
 * the ones that only ship light-on-dark. Never hand-draw a logo.
 */
export const AI_TOOLS = [
  { name: 'Claude', src: '/images/logos/claude-dark.svg', h: 53 },
  { name: 'Codex', src: '/images/logos/openai-dark.svg', h: 67 },
  { name: 'TradingView', src: '/images/logos/tradingview-dark.svg', h: 48 },
  { name: 'Polymarket', src: '/images/logos/polymarket-dark.svg', h: 54 },
];

export const BROKER_LOGOS = [
  { name: 'Schwab', src: '/images/logos/schwab.png', h: 74 },
  { name: 'Alpaca', src: '/images/logos/alpaca.png', h: 75 },
  { name: 'Tradier', src: '/images/logos/tradier-dark.png', h: 78 },
  { name: 'Interactive Brokers', src: '/images/logos/interactive-brokers-dark.png', h: 45 },
  { name: 'Coinbase', src: '/images/logos/coinbase.png', h: 51 },
  { name: 'Bitunix', src: '/images/logos/bitunix.png', h: 51 },
  { name: 'Kraken', src: '/images/logos/kraken.png', h: 47 },
  { name: 'NinjaTrader', src: '/images/logos/ninjatrader.png', h: 39 },
  { name: 'Tradovate', src: '/images/logos/tradeovate-dark.png', h: 68 },
];

// Light-on-dark marks for the Clarity Home "Works with" strip. Dark-ink
// control assets disappear on the dark page, so this set uses the original
// tool SVGs and the catalog broker files instead of the light-band counterparts.
export const CLARITY_LOGOS = [
  { name: 'Claude', src: '/images/logos/claude.svg' },
  { name: 'Codex', src: '/images/logos/openai.svg' },
  { name: 'TradingView', src: '/images/logos/tradingview.svg' },
  { name: 'Polymarket', src: '/images/logos/polymarket.svg' },
  { name: 'Schwab', src: schwabLogo, id: 'schwab' },
  { name: 'Alpaca', src: alpacaDarkBackgroundLogo, id: 'alpaca' },
  { name: 'Tradier', src: tradierLogo, id: 'tradier' },
  { name: 'Interactive Brokers', src: '/images/logos/interactive-brokers.png', id: 'ibkr' },
  { name: 'Coinbase', src: coinbaseLogo, id: 'coinbase' },
  { name: 'Bitunix', src: bitunixLogo, id: 'bitunix' },
  { name: 'Kraken', src: krakenLogo, id: 'kraken' },
  { name: 'NinjaTrader', src: '/images/logos/ninjatrader.png', id: 'ninjatrader' },
  { name: 'Tradovate', src: tradovateLogo, id: 'tradovate' },
];

const LogoRow = ({
  title = "Tools, brokers, and markets you'll work with",
  logos,
  variant = 'control',
  adaptToSystemTheme = false,
}) => {
  const isClarity = variant === 'clarity';
  const marks = logos || (isClarity ? CLARITY_LOGOS : [...AI_TOOLS, ...BROKER_LOGOS]);

  return (
    <section
      className={isClarity ? 'ck-logorow ck-logorow--clarity' : 'ck-logorow'}
      aria-label={title}
      data-testid={isClarity ? 'course-clarity-brokers' : undefined}
    >
      {isClarity ? (
        <>
          <p className="ck-logorow-kicker">Works with</p>
          <ul className="ck-logorow-list">
            {marks.map((logo) => (
              <li
                key={logo.name}
                title={logo.name}
                {...(logo.id ? { 'data-broker': logo.id } : {})}
              >
                {adaptToSystemTheme ? (
                  <picture>
                    <source
                      media="(prefers-color-scheme: light)"
                      srcSet={
                        [...AI_TOOLS, ...BROKER_LOGOS].find((mark) => mark.name === logo.name)
                          ?.src || logo.src
                      }
                    />
                    <img src={logo.src} alt={logo.name} />
                  </picture>
                ) : (
                  <img src={logo.src} alt={logo.name} />
                )}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <Reveal>
            <p className="ck-logorow-title">{title}</p>
          </Reveal>
          <Reveal delay={80}>
            <ul className="ck-logorow-list">
              {marks.map((logo) => (
                <li key={logo.name} title={logo.name}>
                  <img
                    src={logo.src}
                    alt={logo.name}
                    loading="lazy"
                    style={{ height: `${logo.h}px` }}
                  />
                </li>
              ))}
            </ul>
          </Reveal>
        </>
      )}
    </section>
  );
};

export default LogoRow;
